import { createContext, useEffect, useState } from 'react';
import { axiosApi } from '../service/apiEnviornment';
import { useLocation, useNavigate } from 'react-router-dom';
import { retrieveData, storeData, removeData } from '../helper/storageHelper';
import { getPRCount, getJobs } from 'src/service/apiService';
import { normalizeUser } from 'src/utils/authNormalization';
import { recordAudit } from 'src/utils/auditLogger';
import { ACTIVITY_ACTION } from 'src/constants/activityActions';

const defaultProvider = {
  user: null,
  error: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  count: 0,
  upcomingCount: 0,
  countData: () => Promise.resolve(),
  upcomingData: () => Promise.resolve(),
};
const AuthContext = createContext(defaultProvider);

const AuthProvider = ({ children }) => {
  const [count, setCount] = useState();
  const [upcomingCount, setUpcomingCount] = useState();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(defaultProvider.user);
  const [loading, setLoading] = useState(defaultProvider.loading);
  const [error, setError] = useState(defaultProvider.error);
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized);

  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true);
      const storedToken = retrieveData('token');
      const loggedInUser = retrieveData('user');
      if (storedToken && loggedInUser) {
        // Normalize the user object
        const normalized = normalizeUser(loggedInUser);
        if (normalized) {
          // Check if normalization changed the shape
          const isDifferent = JSON.stringify(normalized) !== JSON.stringify(loggedInUser);
          if (isDifferent) {
            // Migration effect: update storage with normalized version
            storeData('user', normalized);
          }
          setUser(normalized);
        } else {
          setUser(loggedInUser);
        }
        setLoading(false);
      } else {
        setUser(null);
        setLoading(false);
        removeData('user');
        removeData('token');
      }
    };
    initAuth();
  }, []);

  const handleLogin = async (data) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      // axiosApi returns resp.data by default
      const resp = await axiosApi('post', '/employee/login', { ...data });
      
      // Helper to extract user name from employee object
      const getUserName = (employee) => employee?.fullName || 'Unknown User';
      
      // Helper to handle successful login
      const handleSuccessfulLogin = (employee, token) => {
        const normalized = normalizeUser(employee);
        const userToStore = normalized || employee;
        storeData('user', userToStore);
        storeData('token', token);
        setUser(userToStore);
        setLoading(false);
        // Log successful login
        const userName = getUserName(userToStore);
        recordAudit({
          action: ACTIVITY_ACTION.LOGIN,
          user: userName,
          message: `${userName} logged in`,
        });
        // Return to short booking links (/b/:code) after login, otherwise go to bookings
        const redirectTo = location.pathname.startsWith('/b/') ? location.pathname : '/booking';
        navigate(redirectTo, { replace: true });
      };
      
      // Expect shape: { employee, token }
      if (resp && resp.employee && resp.token) {
        handleSuccessfulLogin(resp.employee, resp.token);
        return;
      }
      // Backward compatibility if wrapped: { data: { employee, token } }
      if (resp?.data?.employee && resp?.data?.token) {
        handleSuccessfulLogin(resp.data.employee, resp.data.token);
        return;
      }
      throw new Error('Unexpected login response format');
    } catch (err) {
      const status = err?.response?.status;
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : undefined);

      let message = 'Unable to sign in. Please try again.';

      if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
        message = 'Server unavailable. Please check your connection.';
      } else if (status === 0) {
        message = 'Unable to reach the server. Please try again shortly.';
      } else if (status === 404) {
        message = 'Invalid email';
      } else if (status === 401) {
        message = 'Incorrect password';
      } else if (status === 400) {
        message =
          serverMessage || 'Something went wrong with your request. Please review your details and try again.';
      } else if (status === 403) {
        message = 'Your account does not have access. Contact an administrator for help.';
      } else if (status >= 500) {
        message = `Server unavailable. Error code: ${status}`;
      } else if (serverMessage) {
        message = serverMessage;
      } else if (typeof err === 'string') {
        message = err;
      } else if (err?.message) {
        message = err.message;
      }

      setError(message);
      setLoading(false);
      return Promise.reject(message);
    }
  };

  const handleLogout = async () => {
    const userName = user?.fullName || 'Unknown User';
    // Log logout before clearing user data
    recordAudit({
      action: ACTIVITY_ACTION.LOGOUT,
      user: userName,
      message: `${userName} logged out`,
    });
    setUser(null);
    setIsInitialized(false);
    setLoading(false);
    navigate('/', { replace: true });
    removeData('user');
    removeData('token');
  };

  const countData = async () => {
    try {
      const { data } = await getPRCount();
      setCount(data);
    } catch (err) {
      // Silently fail - count errors are not critical
    }
  };

  const upcomingData = async () => {
    try {
      const { data } = await getJobs('upcoming');
      setUpcomingCount(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      // Silently fail - upcoming count errors are not critical
    }
  };

  const values = {
    countData,
    upcomingData,
    count,
    upcomingCount,
    user,
    loading,
    error,
    setError,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
